import React, { useState, useEffect, useRef, useMemo } from 'react';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { useSwipeable } from 'react-swipeable';
import { isGameFinished, BOOKMAKER_ALIASES } from '../utils/constants';
import { isBinaryMarket as isBinaryMarketUtil, getEdgeForPlayerBook } from '../utils/oddsHelpers';
import GameHeader from './GameHeader';
import NCAABGameModal from './NCAABGameModal';

// Create reverse alias map (caesars -> williamhill_us)
const REVERSE_ALIASES = Object.entries(BOOKMAKER_ALIASES).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {});

const NCAABDataTable = ({
  data,
  gameTime,
  gameStatus,
  gameDate,
  lastUpdate,
  game,
  formatPlayerName,
  getTeamLogoUrl,
  getBookLogoUrl,
  isMobile,
  selectedMarket,
  selectedMarketName,
  bookmakers,
  isLoading,
  availableMarkets,
  onMarketSelection,
  showTiers,
  onTierToggle,
  masterTierFilter,
  onPlayerClick,
  onOddsClick,
  onGameClick,
  clickedCellId,
  isCollapsed = false,
  onCollapseToggle,
  ncaabViewMode = 'ou' // 'ou' for O/U view, 'bestline' for Best Line view
}) => {
  // Safety check - provide defaults if game data is not fully loaded
  const safeGame = { away: 'TBD', home: 'TBD', gameIDmlb: 'loading', sport: 'ncaab', ...game };

  // Game modal state
  const [showGameModal, setShowGameModal] = useState(false);

  // Helper function to create cell ID for animation
  const createCellId = (gameId, playerId, market, bookmaker, line, side) => {
    return `${gameId}_${playerId}_${market}_${bookmaker}_${line}_${side}`;
  };

  // Helper function to check if cell should be animated
  const getCellClassName = (gameId, playerId, market, bookmaker, line, side) => {
    const cellId = createCellId(gameId, playerId, market, bookmaker, line, side);
    return cellId === clickedCellId ? 'bet-add-animation' : '';
  };

  // Track rendering state - prevent heavy processing until spinner paints
  const [isRendering, setIsRendering] = useState(false);
  const prevMarketRef = useRef(selectedMarket);

  // Detect market change DURING render (not in useEffect)
  if (prevMarketRef.current !== selectedMarket) {
    if (!isRendering) {
      setIsRendering(true);
    }
    prevMarketRef.current = selectedMarket;
  }

  // Clear rendering state after spinner has painted
  useEffect(() => {
    if (isRendering) {
      const timeoutId = setTimeout(() => {
        setIsRendering(false);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isRendering]);

  // Combined loading state
  const showLoading = isLoading || isRendering;

  // Block rendering heavy data while loading
  const displayData = showLoading ? [] : data;

  // Mobile carousel state
  const [mobileBookIndex, setMobileBookIndex] = useState(0);
  const booksPerPage = 5;

  // Use provided bookmakers or fall back to default list
  const allBookmakers = bookmakers && bookmakers.length > 0 ? bookmakers : ['fanduel', 'draftkings', 'betmgm', 'fanatics'];
  const totalPages = Math.ceil(allBookmakers.length / booksPerPage);

  // Get visible books based on platform
  const bookLogos = isMobile
    ? allBookmakers.slice(mobileBookIndex * booksPerPage, (mobileBookIndex + 1) * booksPerPage)
    : allBookmakers;

  // Navigation functions for mobile
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToPrevBooks = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setMobileBookIndex(prev => (prev === 0 ? totalPages - 1 : prev - 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const goToNextBooks = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setMobileBookIndex(prev => (prev === totalPages - 1 ? 0 : prev + 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };

  // Swipe handlers for mobile
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (isMobile && totalPages > 1) {
        goToNextBooks();
      }
    },
    onSwipedRight: () => {
      if (isMobile && totalPages > 1) {
        goToPrevBooks();
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: false
  });

  const oddsCellStyle = {
    width: '80px',
    minWidth: '80px',
    maxWidth: '80px',
    textAlign: 'center',
    fontSize: '0.95rem',
    letterSpacing: '-0.025em',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  // Use shared utility for consistency with Best Bets
  const isBinaryMarket = isBinaryMarketUtil;

  // =============================================================================
  // PERFORMANCE OPTIMIZATION: Memoized market data processing
  // These computations only re-run when displayData or bookLogos change
  // To revert: remove this useMemo block and restore the inline IIFE in render
  // =============================================================================
  const processedMarketData = useMemo(() => {
    if (!displayData || displayData.length === 0) {
      return { h2hData: {}, spreadsData: {}, totalsData: {}, hasH2H: false, hasSpreads: false, hasTotals: false, h2hArray: [], spreadsArray: [], totalsArray: [] };
    }

    const marketContainer = displayData[0] || {};
    const h2hArray = marketContainer.h2h || [];
    const spreadsArray = marketContainer.spreads || [];
    const totalsArray = marketContainer.totals || [];

    // Helper to get value with alias fallback (caesars -> williamhill_us)
    const getWithAlias = (record, prefix, book, suffix) => {
      let value = record?.[`${prefix}_${book}_${suffix}`];
      if (value !== undefined) return value;
      value = record?.[`${prefix}_${suffix}_${book}`];
      if (value !== undefined) return value;
      const aliasBook = REVERSE_ALIASES[book];
      if (aliasBook) {
        value = record?.[`${prefix}_${aliasBook}_${suffix}`];
        if (value !== undefined) return value;
        value = record?.[`${prefix}_${suffix}_${aliasBook}`];
        if (value !== undefined) return value;
      }
      return undefined;
    };

    // Helper for spreads format
    const getSpreadsValue = (record, book, side) => {
      const checkBook = (b) => {
        const plusIsMain = record?.[`spreads_plus_${side}_${b}_isMain`] === true;
        const minusIsMain = record?.[`spreads_minus_${side}_${b}_isMain`] === true;
        if (plusIsMain) {
          const odds = record?.[`spreads_plus_${side}_${b}`];
          if (odds !== undefined) return odds;
        }
        if (minusIsMain) {
          const odds = record?.[`spreads_minus_${side}_${b}`];
          if (odds !== undefined) return odds;
        }
        return undefined;
      };
      let result = checkBook(book);
      if (result !== undefined) return result;
      const aliasBook = REVERSE_ALIASES[book];
      if (aliasBook) {
        result = checkBook(aliasBook);
        if (result !== undefined) return result;
      }
      return undefined;
    };

    // Helper to check if book has data
    const bookHasData = (record, prefix, book, suffix) => {
      if (prefix === 'spreads') {
        return getSpreadsValue(record, book, suffix) !== undefined;
      }
      return getWithAlias(record, prefix, book, suffix) !== undefined;
    };

    // Helper to check main line for book
    const hasMainLineForBook = (record, marketPrefix, book) => {
      const checkBook = (b) => {
        if (record[`${marketPrefix}_${b}_isAlternate`] === false) return true;
        return Object.keys(record).some(key =>
          key.startsWith(`${marketPrefix}_`) &&
          key.includes(`_${b}`) &&
          key.endsWith('_isMain') &&
          record[key] === true
        );
      };
      if (checkBook(book)) return true;
      const aliasBook = REVERSE_ALIASES[book];
      if (aliasBook && checkBook(aliasBook)) return true;
      return false;
    };

    const hasNonAlternate = (record, prefix, books) => {
      return books.some(book => hasMainLineForBook(record, prefix, book));
    };

    const h2hData = h2hArray.find(m => hasNonAlternate(m, 'h2h', bookLogos)) || {};
    const spreadsData = spreadsArray.find(m => hasNonAlternate(m, 'spreads', bookLogos)) || {};
    const totalsData = totalsArray.find(m => hasNonAlternate(m, 'totals', bookLogos)) || {};

    const hasH2H = bookLogos.some(book => bookHasData(h2hData, 'h2h', book, 'home'));
    const hasSpreads = bookLogos.some(book => bookHasData(spreadsData, 'spreads', book, 'away') || bookHasData(spreadsData, 'spreads', book, 'home'));
    const hasTotals = bookLogos.some(book => bookHasData(totalsData, 'totals', book, 'over'));

    return { h2hData, spreadsData, totalsData, hasH2H, hasSpreads, hasTotals, h2hArray, spreadsArray, totalsArray };
  }, [displayData, bookLogos]);
  // =============================================================================

  // Helper to format odds with +/- sign
  const formatOdds = (odds) => {
    if (odds === null || odds === undefined || odds === '') return '-';
    const numOdds = parseInt(odds, 10);
    if (isNaN(numOdds)) return '-';
    return numOdds > 0 ? `+${numOdds}` : `${numOdds}`;
  };

  // Get best line edge info for a player
  const getBestLineEdge = (player) => {
    return getEdgeForPlayerBook(player, selectedMarket, null, safeGame.sport || 'ncaab');
  };

  // Get best line for a specific book
  const getBestLine = (player, book) => {
    return getEdgeForPlayerBook(player, selectedMarket, book, safeGame.sport || 'ncaab');
  };

  // Cell style for best odds highlighting
  // Only highlight if exactly 1 book has the best price (booksWithBestPrice === 1)
  const getCellStyle = (price, bestBookPrice, bestBookDiff, booksWithBestPrice) => {
    const style = {};

    // Only highlight if this is the best price AND only 1 book has it
    if (price === bestBookPrice && booksWithBestPrice === 1) {
      style.fontWeight = 'bold';

      if (bestBookDiff !== 1) {
        const diffValue = Math.abs(bestBookDiff);
        if (diffValue >= 0.25) {
          Object.assign(style, { backgroundColor: 'green', color: 'yellow' });
        } else if (diffValue >= 0.15) {
          Object.assign(style, { backgroundColor: '#F59E0B', color: '#1E40AF' });
        } else if (diffValue >= 0.05) {
          Object.assign(style, { backgroundColor: '#3B82F6', color: 'white' });
        } else {
          Object.assign(style, { color: '#F59E0B' });
        }
      }
    }

    return style;
  };

  // Format live status for NCAAB games - matches NBA format
  // Two lines: half on first line, clock on second line, both yellow
  const formatLiveStatus = (status, game, safeGame) => {
    // Check for canceled status
    if (status === 'STATUS_CANCELED' || status?.toUpperCase()?.includes('CANCELED')) {
      return 'Canceled';
    }

    // Check for postponed status
    if (status === 'STATUS_POSTPONED' || status?.toUpperCase()?.includes('POSTPONED')) {
      return 'Postponed';
    }

    // Check for halftime status
    if (status === 'STATUS_HALFTIME' || status?.toUpperCase()?.includes('HALFTIME')) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span style={{ color: 'yellow', fontWeight: 600 }}>Halftime</span>
        </div>
      );
    }

    // Check for statusDetail with shortDetail (ESPN format) - parse into period and clock
    if (safeGame.statusDetail?.type?.shortDetail) {
      const shortDetail = safeGame.statusDetail.type.shortDetail;
      // Try to parse "5:32 - 1st Half" format into two lines
      const match = shortDetail.match(/^([\d:]+)\s*-\s*(.+)$/);
      if (match) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <span style={{ color: 'yellow', fontWeight: 600 }}>{match[2]}</span>
            <span style={{ color: 'yellow', fontWeight: 600 }}>{match[1]}</span>
          </div>
        );
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span style={{ color: 'yellow', fontWeight: 600 }}>{shortDetail}</span>
        </div>
      );
    }

    // Use period and clock from game data
    if (safeGame.period || safeGame.clock) {
      const periodText = safeGame.period === 1 ? '1st Half' : safeGame.period === 2 ? '2nd Half' : safeGame.period > 2 ? `OT${safeGame.period - 2}` : '';

      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span style={{ color: 'yellow', fontWeight: 600 }}>{periodText}</span>
          {safeGame.clock && (
            <span style={{ color: 'yellow', fontWeight: 600 }}>{safeGame.clock}</span>
          )}
        </div>
      );
    }

    // Default live indicator
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <span style={{ color: 'yellow', fontWeight: 600 }}>LIVE</span>
      </div>
    );
  };

  return (
    <div className="table-group" {...swipeHandlers}>
      {/* Game Header - shared with NBA */}
      <GameHeader
        gameTime={gameTime}
        gameStatus={gameStatus}
        game={safeGame}
        getTeamLogoUrl={getTeamLogoUrl}
        isMobile={isMobile}
        isCollapsed={isCollapsed}
        onCollapseToggle={onCollapseToggle}
        formatLiveStatus={formatLiveStatus}
        showScores={true}
        isLoading={showLoading}
      />

      {/* Collapsible content */}
      {!isCollapsed && (
        <div>
          {/* Loading spinner */}
          {showLoading && (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm text-secondary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          {/* No data message */}
          {!showLoading && (!displayData || displayData.length === 0) && (
            <div className="text-center py-4 text-muted">
              <small>No odds available for this game yet.</small>
            </div>
          )}

          {/* Mainline markets display */}
          {!showLoading && displayData && displayData.length > 0 && (() => {
            // =============================================================================
            // PERFORMANCE OPTIMIZATION: Use memoized data from processedMarketData
            // Previously this IIFE recomputed all arrays and data on every render
            // To revert: restore the original inline computations from git history
            // =============================================================================
            const { h2hArray, spreadsArray, totalsArray, hasH2H, hasSpreads, hasTotals } = processedMarketData;

            // Helper to check if a book's odds are available (kept inline - cheap operation)
            const isAvailable = (record, prefix, book, suffix) => {
              const key1 = `${prefix}_${book}_${suffix}_isAvailable`;
              const key2 = `${prefix}_${suffix}_${book}_isAvailable`;
              if (record?.[key1] === false || record?.[key2] === false) return false;
              const aliasBook = REVERSE_ALIASES[book];
              if (aliasBook) {
                const aliasKey1 = `${prefix}_${aliasBook}_${suffix}_isAvailable`;
                const aliasKey2 = `${prefix}_${suffix}_${aliasBook}_isAvailable`;
                if (record?.[aliasKey1] === false || record?.[aliasKey2] === false) return false;
              }
              return true;
            };

            // Helper to get value with alias fallback
            const getWithAlias = (record, prefix, book, suffix) => {
              let value = record?.[`${prefix}_${book}_${suffix}`];
              if (value !== undefined) return value;
              value = record?.[`${prefix}_${suffix}_${book}`];
              if (value !== undefined) return value;
              const aliasBook = REVERSE_ALIASES[book];
              if (aliasBook) {
                value = record?.[`${prefix}_${aliasBook}_${suffix}`];
                if (value !== undefined) return value;
                value = record?.[`${prefix}_${suffix}_${aliasBook}`];
                if (value !== undefined) return value;
              }
              return undefined;
            };

            // Helper for spreads format
            const getSpreadsInfo = (record, book, side) => {
              const checkBook = (b) => {
                const plusIsMain = record?.[`spreads_plus_${side}_${b}_isMain`] === true;
                const minusIsMain = record?.[`spreads_minus_${side}_${b}_isMain`] === true;
                if (plusIsMain) {
                  const odds = record?.[`spreads_plus_${side}_${b}`];
                  if (odds !== undefined) return { odds, sign: 'plus' };
                }
                if (minusIsMain) {
                  const odds = record?.[`spreads_minus_${side}_${b}`];
                  if (odds !== undefined) return { odds, sign: 'minus' };
                }
                return null;
              };
              let result = checkBook(book);
              if (result) return result;
              const aliasBook = REVERSE_ALIASES[book];
              if (aliasBook) {
                result = checkBook(aliasBook);
                if (result) return result;
              }
              return null;
            };

            // Helper to get spreads odds value
            const getSpreadsValue = (record, book, side) => {
              const info = getSpreadsInfo(record, book, side);
              return info ? info.odds : undefined;
            };

            // Helper to check if book has data
            const bookHasData = (record, prefix, book, suffix) => {
              if (prefix === 'spreads') {
                return getSpreadsValue(record, book, suffix) !== undefined;
              }
              return getWithAlias(record, prefix, book, suffix) !== undefined;
            };

            // Helper to check main line for book
            const hasMainLineForBook = (record, marketPrefix, book) => {
              const checkBook = (b) => {
                if (record[`${marketPrefix}_${b}_isAlternate`] === false) return true;
                return Object.keys(record).some(key =>
                  key.startsWith(`${marketPrefix}_`) &&
                  key.includes(`_${b}`) &&
                  key.endsWith('_isMain') &&
                  record[key] === true
                );
              };
              if (checkBook(book)) return true;
              const aliasBook = REVERSE_ALIASES[book];
              if (aliasBook && checkBook(aliasBook)) return true;
              return false;
            };

            // Helper to find the main line record for a specific book and side
            const findMainLineForBook = (array, marketPrefix, book, side = null) => {
              return array.find(m => {
                if (!hasMainLineForBook(m, marketPrefix, book)) return false;
                if (side) {
                  return bookHasData(m, marketPrefix, book, side);
                }
                return true;
              }) || null;
            };

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Moneyline Table */}
                {hasH2H && (
                  <div className="table-container">
                    <h6 className="text-center mb-2" style={{ fontSize: '0.9rem', fontWeight: 'bold', color: 'yellow' }}>
                      Moneyline
                    </h6>
                    <Table bordered size="sm" className="text-center">
                      <thead>
                        <tr>
                          <th style={{ width: '120px' }}>TEAM</th>
                          {bookLogos.map(book => (
                            <th key={`h2h-${book}`} style={oddsCellStyle}>
                              <img
                                src={getBookLogoUrl(book)}
                                alt={`${book} logo`}
                                title={book}
                                style={{ height: '20px' }}
                              />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Away Team Row */}
                        <tr>
                          <td style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                              <img
                                src={safeGame.awayLogo || getTeamLogoUrl(safeGame.away)}
                                alt={safeGame.away}
                                style={{ width: '20px', height: '20px' }}
                                onError={(e) => {
                                  e.target.style.visibility = 'hidden';
                                  e.target.style.width = '0px';
                                  e.target.style.height = '0px';
                                }}
                              />
                              {safeGame.away}
                            </div>
                          </td>
                          {(() => {
                            // Count how many books have the best price for h2h away
                            const allOdds = bookLogos.map(b => {
                              const rec = findMainLineForBook(h2hArray, 'h2h', b);
                              return getWithAlias(rec, 'h2h', b, 'away');
                            }).filter(o => o !== undefined && o !== null);
                            const bestPrice = allOdds.length > 0 ? Math.max(...allOdds) : null;
                            const booksWithBestPrice = allOdds.filter(o => o === bestPrice).length;

                            return bookLogos.map(book => {
                              const bookRecord = findMainLineForBook(h2hArray, 'h2h', book);
                              const odds = getWithAlias(bookRecord, 'h2h', book, 'away');
                              const bestBookPrice = bookRecord?.['h2h_bestBookPrice_away'];
                              const bestBookDiff = bookRecord?.['h2h_bestBookDiff_away'];
                              const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, booksWithBestPrice) : {};

                              return (
                                <td key={`h2h-${book}-away`}
                                  className={getCellClassName(safeGame.gameIDmlb, null, 'h2h', book, null, 'away')}
                                  style={{...oddsCellStyle, padding: '4px', cursor: odds ? 'pointer' : undefined, opacity: !isAvailable(bookRecord, 'h2h', book, 'away') ? 0.25 : undefined}}
                                  onClick={() => odds && onOddsClick && onOddsClick(safeGame.gameIDmlb, null, safeGame.awayTeamFull || safeGame.away, 'h2h', book, odds, safeGame.away, safeGame.home, safeGame.awayTeamFull || safeGame.away, null, null, 'away', safeGame.awayTeamId)}
                                >
                                  {odds ? (
                                    <div style={{ ...cellStyle, padding: '4px', borderRadius: '3px', fontSize: '0.9rem' }}>
                                      {odds > 0 ? `+${odds}` : odds}
                                    </div>
                                  ) : null}
                                </td>
                              );
                            });
                          })()}
                        </tr>
                        {/* Home Team Row */}
                        <tr>
                          <td style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                              <img
                                src={safeGame.homeLogo || getTeamLogoUrl(safeGame.home)}
                                alt={safeGame.home}
                                style={{ width: '20px', height: '20px' }}
                                onError={(e) => {
                                  e.target.style.visibility = 'hidden';
                                  e.target.style.width = '0px';
                                  e.target.style.height = '0px';
                                }}
                              />
                              {safeGame.home}
                            </div>
                          </td>
                          {(() => {
                            // Count how many books have the best price for h2h home
                            const allOdds = bookLogos.map(b => {
                              const rec = findMainLineForBook(h2hArray, 'h2h', b);
                              return getWithAlias(rec, 'h2h', b, 'home');
                            }).filter(o => o !== undefined && o !== null);
                            const bestPrice = allOdds.length > 0 ? Math.max(...allOdds) : null;
                            const booksWithBestPrice = allOdds.filter(o => o === bestPrice).length;

                            return bookLogos.map(book => {
                              const bookRecord = findMainLineForBook(h2hArray, 'h2h', book);
                              const odds = getWithAlias(bookRecord, 'h2h', book, 'home');
                              const bestBookPrice = bookRecord?.['h2h_bestBookPrice_home'];
                              const bestBookDiff = bookRecord?.['h2h_bestBookDiff_home'];
                              const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, booksWithBestPrice) : {};

                              return (
                                <td key={`h2h-${book}-home`}
                                  className={getCellClassName(safeGame.gameIDmlb, null, 'h2h', book, null, 'home')}
                                  style={{...oddsCellStyle, padding: '4px', cursor: odds ? 'pointer' : undefined, opacity: !isAvailable(bookRecord, 'h2h', book, 'home') ? 0.25 : undefined}}
                                  onClick={() => odds && onOddsClick && onOddsClick(safeGame.gameIDmlb, null, safeGame.homeTeamFull || safeGame.home, 'h2h', book, odds, safeGame.away, safeGame.home, safeGame.homeTeamFull || safeGame.home, null, null, 'home', safeGame.homeTeamId)}
                                >
                                  {odds ? (
                                    <div style={{ ...cellStyle, padding: '4px', borderRadius: '3px', fontSize: '0.9rem' }}>
                                      {odds > 0 ? `+${odds}` : odds}
                                    </div>
                                  ) : null}
                                </td>
                              );
                            });
                          })()}
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* Totals Table */}
                {hasTotals && (
                  <div className="table-container">
                    <Table bordered size="sm" className="text-center">
                      <thead>
                        <tr><th colSpan={bookLogos.length + 1} style={{ backgroundColor: 'transparent', color: 'yellow', fontWeight: 'bold' }}>Totals</th></tr>
                        <tr>
                          <th style={{ width: '120px' }}></th>
                          {bookLogos.map(book => (
                            <th key={`totals-${book}`} style={oddsCellStyle}>
                              <img
                                src={getBookLogoUrl(book)}
                                alt={`${book} logo`}
                                title={book}
                                style={{ height: '20px' }}
                              />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Over Row */}
                        <tr>
                          <td style={{ width: '120px', fontWeight: 'bold' }}>Over</td>
                          {(() => {
                            // Count how many books have the best price for totals over
                            const allOdds = bookLogos.map(b => {
                              const rec = findMainLineForBook(totalsArray, 'totals', b);
                              return getWithAlias(rec, 'totals', b, 'over');
                            }).filter(o => o !== undefined && o !== null);
                            const bestPrice = allOdds.length > 0 ? Math.max(...allOdds) : null;
                            const booksWithBestPrice = allOdds.filter(o => o === bestPrice).length;

                            return bookLogos.map(book => {
                              const bookRecord = findMainLineForBook(totalsArray, 'totals', book);
                              const odds = getWithAlias(bookRecord, 'totals', book, 'over');
                              const point = bookRecord?.point;
                              const bestBookPrice = bookRecord?.['totals_bestBookPrice_over'];
                              const bestBookDiff = bookRecord?.['totals_bestBookDiff_over'];
                              const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, booksWithBestPrice) : {};

                              return (
                                <td key={`totals-${book}-over`}
                                  className={getCellClassName(safeGame.gameIDmlb, null, 'totals', book, point, 'over')}
                                  style={{...oddsCellStyle, textAlign: 'center', padding: '4px', cursor: odds ? 'pointer' : undefined, opacity: !isAvailable(bookRecord, 'totals', book, 'over') ? 0.25 : undefined}}
                                  onClick={() => odds && onOddsClick && onOddsClick(safeGame.gameIDmlb, null, null, 'totals', book, odds, safeGame.away, safeGame.home, null, null, point, 'over')}
                                >
                                  {odds ? (
                                    <div style={{ ...cellStyle, padding: '4px', borderRadius: '3px' }}>
                                      <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{odds > 0 ? `+${odds}` : odds}</div>
                                      {point && <div style={{ fontSize: '0.75rem', color: cellStyle.color || '#6c757d' }}>o{point}</div>}
                                    </div>
                                  ) : null}
                                </td>
                              );
                            });
                          })()}
                        </tr>

                        {/* Under Row */}
                        <tr>
                          <td style={{ width: '120px', fontWeight: 'bold' }}>Under</td>
                          {(() => {
                            // Count how many books have the best price for totals under
                            const allOdds = bookLogos.map(b => {
                              const rec = findMainLineForBook(totalsArray, 'totals', b);
                              return getWithAlias(rec, 'totals', b, 'under');
                            }).filter(o => o !== undefined && o !== null);
                            const bestPrice = allOdds.length > 0 ? Math.max(...allOdds) : null;
                            const booksWithBestPrice = allOdds.filter(o => o === bestPrice).length;

                            return bookLogos.map(book => {
                              const bookRecord = findMainLineForBook(totalsArray, 'totals', book);
                              const odds = getWithAlias(bookRecord, 'totals', book, 'under');
                              const point = bookRecord?.point;
                              const bestBookPrice = bookRecord?.['totals_bestBookPrice_under'];
                              const bestBookDiff = bookRecord?.['totals_bestBookDiff_under'];
                              const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, booksWithBestPrice) : {};

                              return (
                                <td key={`totals-${book}-under`}
                                  className={getCellClassName(safeGame.gameIDmlb, null, 'totals', book, point, 'under')}
                                  style={{...oddsCellStyle, textAlign: 'center', padding: '4px', cursor: odds ? 'pointer' : undefined, opacity: !isAvailable(bookRecord, 'totals', book, 'under') ? 0.25 : undefined}}
                                  onClick={() => odds && onOddsClick && onOddsClick(safeGame.gameIDmlb, null, null, 'totals', book, odds, safeGame.away, safeGame.home, null, null, point, 'under')}
                                >
                                  {odds ? (
                                    <div style={{ ...cellStyle, padding: '4px', borderRadius: '3px' }}>
                                      {point && <div style={{ fontSize: '0.75rem', color: cellStyle.color || '#6c757d' }}>u{point}</div>}
                                      <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{odds > 0 ? `+${odds}` : odds}</div>
                                    </div>
                                  ) : null}
                                </td>
                              );
                            });
                          })()}
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* Spreads Table */}
                {hasSpreads && (
                  <div className="table-container">
                    <Table bordered size="sm" className="text-center">
                      <thead>
                        <tr><th colSpan={bookLogos.length + 1} style={{ backgroundColor: 'transparent', color: 'yellow', fontWeight: 'bold' }}>Spreads</th></tr>
                        <tr>
                          <th style={{ width: '120px' }}></th>
                          {bookLogos.map(book => (
                            <th key={`spreads-${book}`} style={oddsCellStyle}>
                              <img
                                src={getBookLogoUrl(book)}
                                alt={`${book} logo`}
                                title={book}
                                style={{ height: '20px' }}
                              />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Away Team Row */}
                        <tr>
                          <td style={{ width: '120px', fontWeight: 'bold' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                              <img
                                src={safeGame.awayLogo || getTeamLogoUrl(safeGame.away)}
                                alt={safeGame.away}
                                style={{ width: '16px', height: '16px' }}
                                onError={(e) => {
                                  e.target.style.visibility = 'hidden';
                                  e.target.style.width = '0px';
                                  e.target.style.height = '0px';
                                  e.target.style.margin = '0px';
                                }}
                              />
                              <span>{safeGame.away}</span>
                            </div>
                          </td>
                          {(() => {
                            // Count how many books have the best price for spreads away
                            const allOdds = bookLogos.map(b => {
                              const rec = findMainLineForBook(spreadsArray, 'spreads', b, 'away');
                              return getSpreadsValue(rec, b, 'away');
                            }).filter(o => o !== undefined && o !== null);
                            const bestPrice = allOdds.length > 0 ? Math.max(...allOdds) : null;
                            const booksWithBestPrice = allOdds.filter(o => o === bestPrice).length;

                            return bookLogos.map(book => {
                              const bookRecord = findMainLineForBook(spreadsArray, 'spreads', book, 'away');
                              const spreadsInfo = getSpreadsInfo(bookRecord, book, 'away');
                              const odds = spreadsInfo?.odds;
                              const spreadSign = spreadsInfo?.sign;
                              const point = bookRecord?.point;
                              const bestBookPrice = bookRecord?.['spreads_bestBookPrice_plus_away'];
                              const bestBookDiff = bookRecord?.['spreads_bestBookDiff_plus_away'];
                              const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, booksWithBestPrice) : {};

                              // Format point based on the isMain sign (plus = +point, minus = -point)
                              const displayPoint = point !== null && point !== undefined
                                ? (spreadSign === 'minus' ? `-${point}` : `+${point}`)
                                : null;

                              return (
                                <td key={`spreads-${book}-away`}
                                  className={getCellClassName(safeGame.gameIDmlb, null, 'spreads', book, displayPoint, 'away')}
                                  style={{...oddsCellStyle, textAlign: 'center', padding: '4px', cursor: odds ? 'pointer' : undefined, opacity: !isAvailable(bookRecord, 'spreads', book, 'away') ? 0.25 : undefined}}
                                  onClick={() => odds && onOddsClick && onOddsClick(safeGame.gameIDmlb, null, safeGame.awayTeamFull || safeGame.away, 'spreads', book, odds, safeGame.away, safeGame.home, safeGame.awayTeamFull || safeGame.away, null, displayPoint, 'away', safeGame.awayTeamId)}
                                >
                                  {odds ? (
                                    <div style={{ ...cellStyle, padding: '4px', borderRadius: '3px' }}>
                                      <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{odds > 0 ? `+${odds}` : odds}</div>
                                      {displayPoint && <div style={{ fontSize: '0.75rem', color: cellStyle.color || '#6c757d' }}>{displayPoint}</div>}
                                    </div>
                                  ) : null}
                                </td>
                              );
                            });
                          })()}
                        </tr>

                        {/* Home Team Row */}
                        <tr>
                          <td style={{ width: '120px', fontWeight: 'bold' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                              <img
                                src={safeGame.homeLogo || getTeamLogoUrl(safeGame.home)}
                                alt={safeGame.home}
                                style={{ width: '16px', height: '16px' }}
                                onError={(e) => {
                                  e.target.style.visibility = 'hidden';
                                  e.target.style.width = '0px';
                                  e.target.style.height = '0px';
                                  e.target.style.margin = '0px';
                                }}
                              />
                              <span>{safeGame.home}</span>
                            </div>
                          </td>
                          {(() => {
                            // Count how many books have the best price for spreads home
                            const allOdds = bookLogos.map(b => {
                              const rec = findMainLineForBook(spreadsArray, 'spreads', b, 'home');
                              return getSpreadsValue(rec, b, 'home');
                            }).filter(o => o !== undefined && o !== null);
                            const bestPrice = allOdds.length > 0 ? Math.max(...allOdds) : null;
                            const booksWithBestPrice = allOdds.filter(o => o === bestPrice).length;

                            return bookLogos.map(book => {
                              const bookRecord = findMainLineForBook(spreadsArray, 'spreads', book, 'home');
                              const spreadsInfo = getSpreadsInfo(bookRecord, book, 'home');
                              const odds = spreadsInfo?.odds;
                              const spreadSign = spreadsInfo?.sign;
                              const point = bookRecord?.point;
                              const bestBookPrice = bookRecord?.['spreads_bestBookPrice_minus_home'];
                              const bestBookDiff = bookRecord?.['spreads_bestBookDiff_minus_home'];
                              const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, booksWithBestPrice) : {};

                              // Format point based on the isMain sign (plus = +point, minus = -point)
                              const displayPoint = point !== null && point !== undefined
                                ? (spreadSign === 'minus' ? `-${point}` : `+${point}`)
                                : null;

                              return (
                                <td key={`spreads-${book}-home`}
                                  className={getCellClassName(safeGame.gameIDmlb, null, 'spreads', book, displayPoint, 'home')}
                                  style={{...oddsCellStyle, textAlign: 'center', padding: '4px', cursor: odds ? 'pointer' : undefined, opacity: !isAvailable(bookRecord, 'spreads', book, 'home') ? 0.25 : undefined}}
                                  onClick={() => odds && onOddsClick && onOddsClick(safeGame.gameIDmlb, null, safeGame.homeTeamFull || safeGame.home, 'spreads', book, odds, safeGame.away, safeGame.home, safeGame.homeTeamFull || safeGame.home, null, displayPoint, 'home', safeGame.homeTeamId)}
                                >
                                  {odds ? (
                                    <div style={{ ...cellStyle, padding: '4px', borderRadius: '3px' }}>
                                      <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{odds > 0 ? `+${odds}` : odds}</div>
                                      {displayPoint && <div style={{ fontSize: '0.75rem', color: cellStyle.color || '#6c757d' }}>{displayPoint}</div>}
                                    </div>
                                  ) : null}
                                </td>
                              );
                            });
                          })()}
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            );
          })()}

          {/* View All Lines Button */}
          <div className="text-center mt-3">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => onGameClick ? onGameClick(safeGame.gameIDmlb) : setShowGameModal(true)}
              style={{ fontSize: '0.8rem' }}
            >
              View All Lines
            </button>
          </div>
        </div>
      )}

      {/* Game Modal for viewing all lines */}
      <NCAABGameModal
        show={showGameModal}
        onHide={() => setShowGameModal(false)}
        game={safeGame}
        gameId={safeGame.gameIDmlb}
        getTeamLogoUrl={getTeamLogoUrl}
        getBookLogoUrl={getBookLogoUrl}
        bookmakers={bookmakers}
      />
    </div>
  );
};

// =============================================================================
// PERFORMANCE OPTIMIZATION: React.memo prevents re-renders when props unchanged
// Custom comparator checks only props that affect rendering
// To revert: change to `export default NCAABDataTable;`
// =============================================================================
export default React.memo(NCAABDataTable, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render)
  // Return false if props changed (trigger re-render)

  // Always re-render if data changed
  if (prevProps.data !== nextProps.data) return false;

  // Re-render if collapse state changed
  if (prevProps.isCollapsed !== nextProps.isCollapsed) return false;

  // Re-render if game status changed (for live games)
  if (prevProps.gameStatus !== nextProps.gameStatus) return false;

  // Re-render if loading state changed
  if (prevProps.isLoading !== nextProps.isLoading) return false;

  // Re-render if bookmakers list changed
  if (prevProps.bookmakers !== nextProps.bookmakers) return false;

  // Re-render if mobile state changed
  if (prevProps.isMobile !== nextProps.isMobile) return false;

  // Re-render if clicked cell changed (for animations)
  if (prevProps.clickedCellId !== nextProps.clickedCellId) return false;

  // Props are equal, skip re-render
  return true;
});
